import { Space, Drawer, Button, Col, Form, Row, Input, message, Select, Upload, Flex, Avatar, Typography, Divider } from "antd";
import { useEffect, useState } from "react";
import { createInvestmentIdea, updateInvestmentIdea } from "../../api/ai";
import { firebaseConfig, BucketType } from '../../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getFirebaseFilename, uuid } from "../../utils/comm";
import { CloseOutlined } from "@ant-design/icons";
import { countryTuples } from 'country-region-data';
import { DatePicker } from "antd";

const InvestmentIdeaDrawView = ({ open, defaultIdea, retryInfo, onClose, onUpdate, editProofOnly = false, actionType = null, }) => {
    const [idea, setIdea] = useState({
        ticker: null,
        triggerTag: null,
        event: null,
        sector: null,
        ideaType: null,
        rating: null,
        suggestion: null,
        description: null,
        weight: null,
        category: null,
        votes: null,
        status: null,
        country: null,
        goaiConviction: null,
        targetPrice: null,
        targetPriceHorizon: null,
        priceAtCreation: null,
        lockedPerformance: null,
        exitDetail: null,
    });
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [form] = Form.useForm();
    // 保存原始值用于 submitChange 时自动设置 versionTag
    const [originalValues, setOriginalValues] = useState({
        targetPrice: null,
        aiReportStopLossPrice: null,
        aiReportTimeHorizonDays: null,
        goaiConviction: null,
    });
    const countries = countryTuples.map(item => {
        return {
            label: item[0].split(',')[0],
            value: item[0],
            flag: item[1]
        }
    })

    useEffect(() => {
        setIdea(defaultIdea ?? {});
        if (defaultIdea && Object.keys(defaultIdea).length > 0) {
            form.setFieldValue('ticker', defaultIdea.ticker);
            form.setFieldValue('triggerTag', defaultIdea.triggerTag);
            form.setFieldValue('targetPrice', defaultIdea.targetPrice);
            form.setFieldValue('event', defaultIdea.event);
            form.setFieldValue('sector', defaultIdea.sector);
            form.setFieldValue('ideaType', defaultIdea.ideaType);
            form.setFieldValue('rating', defaultIdea.rating);
            form.setFieldValue('suggestion', defaultIdea.suggestion);
            form.setFieldValue('description', defaultIdea.description);
            form.setFieldValue('weight', defaultIdea.weight);
            form.setFieldValue('category', defaultIdea.category);
            form.setFieldValue('votes', defaultIdea.votes);
            form.setFieldValue('status', defaultIdea.status);
            form.setFieldValue('country', defaultIdea.country);
            form.setFieldValue('goaiConviction', defaultIdea.goaiConviction);
            form.setFieldValue('targetPriceHorizon', defaultIdea.targetPriceHorizon);
            form.setFieldValue('priceAtCreation', defaultIdea.priceAtCreation);
            form.setFieldValue('lockedPerformance', defaultIdea.lockedPerformance ? (defaultIdea.lockedPerformance / 100) : null);

            // 解析 aiReportTxt JSON 字符串
            let aiReportStopLossPrice = null;
            let aiReportTimeHorizonDays = null;
            if (defaultIdea.aiReportTxt) {
                try {
                    const aiReport = JSON.parse(defaultIdea.aiReportTxt);
                    aiReportStopLossPrice = aiReport.stop_loss_price;
                    aiReportTimeHorizonDays = aiReport.time_horizon_days;
                    form.setFieldValue('aiReportStopLossPrice', aiReport.stop_loss_price);
                    form.setFieldValue('aiReportTimeHorizonDays', aiReport.time_horizon_days);
                    form.setFieldValue('aiReportSummary', aiReport.summary);
                    form.setFieldValue('aiReportRecommendedAction', aiReport.recommended_action);
                } catch (error) {
                    console.error('Failed to parse aiReportTxt:', error);
                }
            }

            // 保存原始值用于 submitChange 时自动设置 versionTag
            setOriginalValues({
                targetPrice: defaultIdea.targetPrice,
                aiReportStopLossPrice: aiReportStopLossPrice,
                aiReportTimeHorizonDays: aiReportTimeHorizonDays,
                goaiConviction: defaultIdea.goaiConviction,
            });

            // 解析 exitDetail JSON 字符串
            if (defaultIdea.exitDetail) {
                try {
                    const exitDetail = JSON.parse(defaultIdea.exitDetail);
                    form.setFieldValue('exitDetailReason', exitDetail.reason);
                    form.setFieldValue('exitDetailPrice', exitDetail.price);
                    form.setFieldValue('exitDetailNote', exitDetail.note);
                } catch (error) {
                    console.error('Failed to parse exitDetail:', error);
                }
            }

            // 当 actionType 是 submitExit 或 submitExitDiscardChange 时，自动填充 locked performance 和 exit price
            if (actionType === 'submitExit' || actionType === 'submitExitDiscardChange') {
                // 自动填充 locked performance (pricePerformance / 100)
                if (defaultIdea.pricePerformance !== null && defaultIdea.pricePerformance !== undefined) {
                    form.setFieldValue('lockedPerformance', (defaultIdea.pricePerformance / 100).toFixed(2));
                }
                // 自动填充 exit price (priceAtNow)
                if (defaultIdea.priceAtNow) {
                    form.setFieldValue('exitDetailPrice', defaultIdea.priceAtNow);
                }
            }

            // 初始化 proofOfWork 字段
            form.setFieldValue('proofOfWork', defaultIdea.proof?.proofOfWork ?? []);
        } else if (retryInfo && Object.keys(retryInfo).length > 0) {
            let additional = {};
            try {
                additional = JSON.parse(retryInfo.additional)
            } catch (error) { }
            form.setFieldValue('ticker', retryInfo.ticker);
            form.setFieldValue('triggerTag', retryInfo.triggerTag);
            form.setFieldValue('event', additional?.event);
            form.setFieldValue('sector', retryInfo.category);

            form.setFieldValue('attachments', (additional?.attachments ?? []).map((item, index) => ({
                mimeType: item.mimeType,
                url: [
                    {
                        uid: `${uuid()}-${index}`,
                        name: getFirebaseFilename(item.url),
                        status: 'done',
                        url: item.url,
                        file: {
                            response: {
                                url: item.url
                            }
                        }
                    }
                ]
            })));
        } else {
            resetFields();
        }
    }, [defaultIdea, open])

    const resetFields = () => {
        form.setFieldValue('ticker', null);
        form.setFieldValue('triggerTag', null);
        form.setFieldValue('targetPrice', null);
        form.setFieldValue('targetPriceHorizon', null);
        form.setFieldValue('event', null);
        form.setFieldValue('sector', null);
        form.setFieldValue('ideaType', null);
        form.setFieldValue('rating', null);
        form.setFieldValue('suggestion', null);
        form.setFieldValue('description', null);
        form.setFieldValue('weight', null);
        form.setFieldValue('category', null);
        form.setFieldValue('votes', null);
        form.setFieldValue('status', null);
        form.setFieldValue('attachments', []);
        form.setFieldValue('country', null);
        form.setFieldValue('goaiConviction', null);
        form.setFieldValue('priceAtCreation', null);
        form.setFieldValue('lockedPerformance', null);
        form.setFieldValue('exitDetail', null);
        form.setFieldValue('proofOfWork', []);
        form.setFieldValue('versionTag', null);
        form.setFieldValue('versionTitle', null);
        form.setFieldValue('versionBody', null);
    }

    // 自动更新 versionTag 根据字段变化
    const updateVersionTagBasedOnChanges = () => {
        if (actionType !== 'submitChange') return;

        const currentTargetPrice = form.getFieldValue('targetPrice');
        const currentStopLoss = form.getFieldValue('aiReportStopLossPrice');
        const currentHorizon = form.getFieldValue('aiReportTimeHorizonDays');
        const currentConviction = form.getFieldValue('goaiConviction');

        const changes = [];

        // 检查 targetPrice 是否变化
        if (currentTargetPrice !== originalValues.targetPrice && currentTargetPrice) {
            changes.push('targetPrice');
        }

        // 检查 stopLoss 是否变化
        if (currentStopLoss !== originalValues.aiReportStopLossPrice && currentStopLoss) {
            changes.push('stopLoss');
        }

        // 检查 horizon 是否变化
        if (currentHorizon !== originalValues.aiReportTimeHorizonDays && currentHorizon) {
            const oldHorizon = parseInt(originalValues.aiReportTimeHorizonDays) || 0;
            const newHorizon = parseInt(currentHorizon) || 0;
            if (newHorizon > oldHorizon) {
                changes.push('horizonExtended');
            } else if (newHorizon < oldHorizon) {
                changes.push('horizonShortened');
            }
        }

        // 检查 conviction 是否变化
        if (currentConviction !== originalValues.goaiConviction && currentConviction) {
            changes.push('conviction');
        }

        // 根据变化数量设置 versionTag
        if (changes.length === 0) {
            return; // 没有变化，不设置
        } else if (changes.length > 1) {
            form.setFieldValue('versionTag', 'Multiple Adjustment');
        } else {
            // 只有一个变化
            const change = changes[0];
            switch (change) {
                case 'targetPrice':
                    form.setFieldValue('versionTag', 'Target Price Adjusted');
                    break;
                case 'stopLoss':
                    form.setFieldValue('versionTag', 'Stop-Loss Adjusted');
                    break;
                case 'horizonExtended':
                    form.setFieldValue('versionTag', 'Horizon Extended');
                    break;
                case 'horizonShortened':
                    form.setFieldValue('versionTag', 'Horizon Shortened');
                    break;
                case 'conviction':
                    form.setFieldValue('versionTag', 'Conviction Level Changed');
                    break;
                default:
                    break;
            }
        }
    };

    // 根据 Exit Reason 自动设置 versionTag 和 versionTitle
    const updateVersionTagBasedOnExitReason = (exitReason) => {
        if (actionType !== 'submitExit' && actionType !== 'submitExitDiscardChange') return;

        switch (exitReason) {
            case 'bad_news':
                form.setFieldValue('versionTag', 'Closed');
                break;
            case 'stop_loss':
                form.setFieldValue('versionTag', 'Closed');
                form.setFieldValue('versionTitle', 'Stop Loss Hit');
                break;
            case 'target_hit':
                form.setFieldValue('versionTag', 'Closed');
                form.setFieldValue('versionTitle', 'Target Price Hit');
                break;
            case 'time_expired':
                form.setFieldValue('versionTag', 'Closed');
                form.setFieldValue('versionTitle', 'Horizon Ended');
                break;
            default:
                break;
        }
    };

    const onSubmit = () => {
        let params = {};
        if (defaultIdea) {
            // 根据 actionType 构建不同的参数
            if (actionType === 'submitChange') {
                // 4.4 提交变更审核 (1 → 3)
                // 只更新特定字段：id, status, targetPrice, aiReportTxt, versionTag, versionTitle, versionBody
                params = {
                    id: idea.id,
                    status: 3,
                    targetPrice: form.getFieldValue('targetPrice'),
                };

                const aiReportStopLossPrice = form.getFieldValue('aiReportStopLossPrice');
                const aiReportTimeHorizonDays = form.getFieldValue('aiReportTimeHorizonDays');
                const aiReportSummary = form.getFieldValue('aiReportSummary');
                const aiReportRecommendedAction = form.getFieldValue('aiReportRecommendedAction');

                if (aiReportStopLossPrice || aiReportTimeHorizonDays || aiReportSummary || aiReportRecommendedAction) {
                    params.aiReportTxt = JSON.stringify({
                        ...JSON.parse(defaultIdea.aiReportTxt),
                        stop_loss_price: aiReportStopLossPrice || '',
                        time_horizon_days: aiReportTimeHorizonDays ? parseInt(aiReportTimeHorizonDays) : '',
                        summary: aiReportSummary || '',
                        recommended_action: aiReportRecommendedAction || ''
                    });
                }

                // Version history fields
                const versionTag = form.getFieldValue('versionTag');
                const versionTitle = form.getFieldValue('versionTitle');
                const versionBody = form.getFieldValue('versionBody');
                if (versionTag) params.versionTag = versionTag;
                if (versionTitle) params.versionTitle = versionTitle;
                if (versionBody) params.versionBody = versionBody;
            } else if (actionType === 'submitExit' || actionType === 'submitExitDiscardChange') {
                // 4.7 提交退出审核 (1 → 4) 或 放弃变更并提交退出审核 (3 → 4)
                // 只更新特定字段：id, status, lockedPerformance, exitDetail, versionTag, versionTitle, versionBody
                params = {
                    id: idea.id,
                    status: 4,
                };

                const lockedPerformance = form.getFieldValue('lockedPerformance');
                if (lockedPerformance && parseFloat(lockedPerformance) != 0) {
                    params.lockedPerformance = Math.round(parseFloat(lockedPerformance) * 100);
                }

                const exitDetailReason = form.getFieldValue('exitDetailReason');
                const exitDetailPrice = form.getFieldValue('exitDetailPrice');
                const exitDetailNote = form.getFieldValue('exitDetailNote');

                if (exitDetailReason || exitDetailPrice || exitDetailNote) {
                    params.exitDetail = JSON.stringify({
                        reason: exitDetailReason || '',
                        price: exitDetailPrice || '',
                        note: exitDetailNote || ''
                    });
                }

                // Version history fields
                const versionTag = form.getFieldValue('versionTag');
                const versionTitle = form.getFieldValue('versionTitle');
                const versionBody = form.getFieldValue('versionBody');
                if (versionTag) params.versionTag = versionTag;
                if (versionTitle) params.versionTitle = versionTitle;
                if (versionBody) params.versionBody = versionBody;
            } else {
                // 常规编辑（创建或编辑草稿）
                params = {
                    ...idea,
                    weight: parseInt(form.getFieldValue('weight') ?? '0'),
                    category: form.getFieldValue('category'),
                    votes: form.getFieldValue('votes'),
                    ideaType: form.getFieldValue('ideaType'),
                    ticker: form.getFieldValue('ticker'),
                    rating: form.getFieldValue('rating'),
                    suggestion: form.getFieldValue('suggestion'),
                    description: form.getFieldValue('description'),
                    triggerTag: form.getFieldValue('triggerTag'),
                    country: form.getFieldValue('country'),
                    goaiConviction: form.getFieldValue('goaiConviction'),
                    targetPrice: form.getFieldValue('targetPrice'),
                    targetPriceHorizon: form.getFieldValue('targetPriceHorizon'),
                    priceAtCreation: form.getFieldValue('priceAtCreation'),
                };
                // 基础参数
                delete params['lockedPerformance'];
            }

            // 添加 proof 字段（仅当 editProofOnly 为 true 时）
            if (editProofOnly) {
                params.proof = {
                    proofOfWork: form.getFieldValue('proofOfWork') ?? []
                };
            }
        } else {
            var attachments = form.getFieldValue('attachments')?.map(item => {
                let url = item.url;
                if (Array.isArray(url) && url.length > 0) {
                    if (url[0].url) {
                        url = url[0].url;
                    } else if (url[0].file?.response?.url) {
                        url = url[0].file.response.url;
                    } else if (url[0].response?.url) {
                        url = url[0].response.url;
                    }
                }
                return {
                    mimeType: item.mimeType,
                    url: url,
                }
            })
            params = {
                ticker: form.getFieldValue('ticker'),
                triggerTag: form.getFieldValue('triggerTag'),
                event: form.getFieldValue('event'),
                sector: form.getFieldValue('sector'),
                attachments: attachments,
            }
            if (retryInfo) {
                params.id = retryInfo.id;
            }
        }
        if (defaultIdea?.id) {
            setLoading(true);
            updateInvestmentIdea(params).then(res => {
                setLoading(false);
                setIdea({});
                resetFields();

                onClose();
                onUpdate();
            }).catch(err => {
                message.error(String(err));
                setLoading(false);
            });
        } else {
            if (!params.ticker) {
                message.warning('Please input ticker');
                return;
            }
            if (!params.event || !params.event.trim()) {
                message.warning('Please input event');
                return;
            }
            if (!params.triggerTag || !params.triggerTag.trim()) {
                message.warning('Please input trigger tag');
                return;
            }
            setLoading(true);

            createInvestmentIdea(params).then(res => {
                setLoading(false);
                setIdea({});
                resetFields();

                onClose();
                onUpdate();
            }).catch(err => {
                message.error(String(err));
                setLoading(false);
            });
        }

    }

    const handleUpload = (options, subFieldName) => {
        let file = options.file;
        let fileKey = `${file.uid}-${file.name}`; // 使用文件 uid 和名称作为唯一标识
        let fileName = `${uuid()}-${file.name}`;
        let storage = firebaseConfig(String(file.type).includes('video') ? BucketType.video : BucketType.image);
        const storageRef = ref(storage, `/investment_files/${encodeURIComponent(fileName.replace(/[ !@#$%^&*(),?":{}|<>]/g, '-'))}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
            "state_changed",
            (snapshot) => {
                // 计算上传进度百分比
                const percent = Math.round(
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                );
                // 更新进度状态
                setUploadProgress(prev => ({
                    ...prev,
                    [fileKey]: percent
                }));
                // 将进度传递给 Upload 组件
                options.onProgress({ percent });
            },
            (err) => {
                message.error(String(err));
                options.onError(err);
                // 清除该文件的进度记录
                setUploadProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[fileKey];
                    return newProgress;
                });
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((url) => {
                    // 构建 fileList 格式的文件对象
                    const fileObj = {
                        uid: file.uid,
                        name: file.name,
                        status: 'done',
                        url: url,
                        file: {
                            response: {
                                url: url
                            }
                        }
                    };
                    // 将文件对象设置为 fileList 数组
                    form.setFieldValue([...subFieldName], [fileObj]);
                    // 触发 Upload 组件的成功回调
                    options.onSuccess({ url }, file);
                    // 清除该文件的进度记录
                    setUploadProgress(prev => {
                        const newProgress = { ...prev };
                        delete newProgress[fileKey];
                        return newProgress;
                    });
                });
            }
        );
    }

    return (
        <div>
            <Drawer
                forceRender={true}
                title={editProofOnly ? 'Edit Proof of Work' : (defaultIdea ? 'Edit Investment Idea' : 'Create Investment Idea')}
                width={560}
                maskClosable
                onClose={onClose}
                open={open}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
                extra={
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button onClick={onSubmit} type="primary" loading={loading}>
                            Submit
                        </Button>
                    </Space>
                }
            >
                <Form
                    layout="vertical"
                    form={form}
                    requiredMark={false} >
                    {
                        !editProofOnly && !actionType && (
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item name='ticker' label='Ticker'>
                                        <Input placeholder="ticker" allowClear />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )
                    }
                    {
                        defaultIdea ?
                            (!editProofOnly && !actionType && <>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name='ideaType' label='Type'>
                                            <Select options={[
                                                { key: 1, label: 'AI Pick', value: 0 },
                                                { key: 2, label: 'Social Trader', value: 1 },
                                            ]}
                                                placeholder="select type"
                                                allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name='goaiConviction' label='GoAI Conviction'>
                                            <Select options={[
                                                { key: 1, label: 'GoAI Alpha', value: 'GoAI Alpha' },
                                                { key: 2, label: 'High Conviction', value: 'High Conviction' },
                                                { key: 2, label: 'Medium Conviction', value: 'Medium Conviction' },
                                            ]}
                                                placeholder="select conviction"
                                                allowClear
                                                onChange={() => setTimeout(updateVersionTagBasedOnChanges, 0)} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name='targetPrice' label='Target Price'>
                                            <Input placeholder="target price" allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name='targetPriceHorizon' label='Target Price Horizon'>
                                            <Input placeholder="target price horizon" allowClear />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name='priceAtCreation' label='Price At Creation'>
                                            <Input placeholder="price at creation" allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name='country' label='Country'>
                                            <Select
                                                placeholder="country/city select..."
                                                options={countries}
                                                optionRender={(option) => {
                                                    let flag = `http://purecatamphetamine.github.io/country-flag-icons/3x2/${option.data.flag}.svg`;
                                                    return (
                                                        <Space size={[4, 0]}>
                                                            <Avatar src={flag} size={16} />
                                                            <Typography.Text style={{ fontSize: 12 }}>
                                                                <b>{option.data.label}</b>
                                                            </Typography.Text>
                                                        </Space>
                                                    )
                                                }}
                                                allowClear
                                                showSearch />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name='rating' label='GoAI Rating'>
                                            <Select options={[
                                                { key: 1, label: '1', value: 1 },
                                                { key: 2, label: '2', value: 2 },
                                                { key: 3, label: '3', value: 3 },
                                                { key: 4, label: '4', value: 4 },
                                                { key: 5, label: '5', value: 5 },
                                            ]}
                                                placeholder="AI rating"
                                                allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name='suggestion' label='Suggestion'>
                                            <Select options={[
                                                { key: 1, label: 'Bullish', value: 0 },
                                                { key: 2, label: 'Bearish', value: 1 },
                                            ]}
                                                placeholder="suggestion"
                                                allowClear />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name='triggerTag' label='Trigger Tag'>
                                            <Input placeholder="trigger tag" allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name='weight' label='Weight'>
                                            <Input placeholder="weight" allowClear type='number' />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name='category' label='Category'>
                                            <Input placeholder="category" allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name='votes' label='Votes'>
                                            <Input placeholder="votes" allowClear type='number' />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </>)
                            :
                            <>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item name='triggerTag' label='Trigger Tag'>
                                            <Input placeholder="trigger tag" allowClear />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item name='event' label='Event'>
                                            <Input.TextArea placeholder="event" allowClear rows={5} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item name='sector' label='Sector'>
                                            <Input.TextArea placeholder="sector" allowClear rows={5} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.List name={'attachments'}>
                                            {(subFields, subOpt) => (
                                                <div style={{ display: 'flex', flexDirection: 'column', rowGap: 16 }}>
                                                    {subFields.map((subField) => (
                                                        <Flex key={subField.key} style={{ padding: 12, border: '1px dashed #eee' }}>
                                                            <div style={{ flex: 1, marginRight: 12 }}>
                                                                <div style={{ marginBottom: 12 }}>
                                                                    <Form.Item label='File Type' noStyle name={[subField.name, 'mimeType']}>
                                                                        <Select options={[
                                                                            { key: 1, label: 'Application/pdf', value: 'application/pdf' },
                                                                            { key: 2, label: 'Text/plain', value: 'text/plain' },]}
                                                                            placeholder="Attachments type"
                                                                            allowClear />
                                                                    </Form.Item>
                                                                </div>

                                                                <div>
                                                                    <Form.Item
                                                                        label='File Source'
                                                                        noStyle
                                                                        name={[subField.name, 'url']}
                                                                        valuePropName="fileList"
                                                                        getValueFromEvent={(e) => {
                                                                            if (Array.isArray(e)) {
                                                                                return e;
                                                                            }
                                                                            return e?.fileList;
                                                                        }}
                                                                    >
                                                                        <Upload
                                                                            maxCount={1}
                                                                            multiple={false}
                                                                            customRequest={(options) => handleUpload(options, [subField.name, 'url'])}
                                                                        >
                                                                            <Button>Upload File</Button>
                                                                        </Upload>
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                            <CloseOutlined
                                                                onClick={() => {
                                                                    subOpt.remove(subField.name);
                                                                }}
                                                            />
                                                        </Flex>
                                                    ))}
                                                    <Button style={{ width: 200, fontSize: 12 }} type="dashed" onClick={() => subOpt.add()} block>
                                                        + Add File
                                                    </Button>
                                                </div>
                                            )}
                                        </Form.List>
                                    </Col>
                                </Row>
                            </>
                    }
                    {
                        // 状态转换所需字段
                        defaultIdea && !editProofOnly && (
                            <>
                                {/* 提交变更审核 (1 → 3) */}
                                {actionType === 'submitChange' && (
                                    <>
                                        <Divider>Status Transition Fields</Divider>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='targetPrice' label='Target Price'>
                                                    <Input placeholder="target price" allowClear onChange={() => setTimeout(updateVersionTagBasedOnChanges, 0)} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item name='aiReportStopLossPrice' label='Stop Loss Price'>
                                                    <Input placeholder="e.g., 145.00" allowClear onChange={() => setTimeout(updateVersionTagBasedOnChanges, 0)} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name='aiReportTimeHorizonDays' label='Time Horizon (Days)'>
                                                    <Input placeholder="e.g., 45" allowClear onChange={() => setTimeout(updateVersionTagBasedOnChanges, 0)} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='aiReportSummary' label='Summary'>
                                                    <Input.TextArea placeholder="summary" rows={2} allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='aiReportRecommendedAction' label='Recommended Action'>
                                                    <Input placeholder="e.g., Buy, Strong Buy, Hold, Sell" allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Divider>Version History</Divider>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='versionTag' label='Version Tag'>
                                                    <Select options={[
                                                        { key: 1, label: 'Initiated', value: 'Initiated' },
                                                        { key: 2, label: 'Target Price Adjusted', value: 'Target Price Adjusted' },
                                                        { key: 3, label: 'Stop-Loss Adjusted', value: 'Stop-Loss Adjusted' },
                                                        { key: 4, label: 'Horizon Extended', value: 'Horizon Extended' },
                                                        { key: 5, label: 'Horizon Shortened', value: 'Horizon Shortened' },
                                                        { key: 6, label: 'Conviction Level Changed', value: 'Conviction Level Changed' },
                                                        { key: 7, label: 'Thesis Paradigm Shift', value: 'Thesis Paradigm Shift' },
                                                        { key: 8, label: 'Multiple Adjustment', value: 'Multiple Adjustment' },
                                                        { key: 9, label: 'Closed', value: 'Closed' },
                                                    ]}
                                                        placeholder="select version tag"
                                                        allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='versionTitle' label='Version Title'>
                                                    <Input placeholder="e.g., Target Price change to $200" allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='versionBody' label='Version Body'>
                                                    <Input.TextArea placeholder="Details about the change" rows={3} allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                                {/* 提交退出审核 (1 → 4) 或 放弃变更并提交退出审核 (3 → 4) */}
                                {(actionType === 'submitExit' || actionType === 'submitExitDiscardChange') && (
                                    <>
                                        <Divider>Status Transition Fields</Divider>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='lockedPerformance' label='Locked Performance (%)'>
                                                    <Input placeholder="e.g., 12.5 for 12.5%" allowClear type='number' step='0.01' />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item name='exitDetailReason' label='Exit Reason' rules={[{ required: true, message: 'Please select exit reason' }]}>
                                                    <Select options={[
                                                        { key: 1, label: 'Bad News', value: 'bad_news' },
                                                        { key: 2, label: 'Target Hit', value: 'target_hit' },
                                                        { key: 3, label: 'Stop Loss', value: 'stop_loss' },
                                                        { key: 4, label: 'Time Expired', value: 'time_expired' },
                                                    ]}
                                                        placeholder="select reason"
                                                        allowClear
                                                        onChange={(value) => updateVersionTagBasedOnExitReason(value)} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name='exitDetailPrice' label='Exit Price'>
                                                    <Input placeholder="e.g., 180.00" allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='exitDetailNote' label='Note'>
                                                    <Input placeholder="e.g., 目标价已达成" allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Divider>Version History</Divider>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='versionTag' label='Version Tag'>
                                                    <Select options={[
                                                        { key: 1, label: 'Closed', value: 'Closed' },
                                                        { key: 2, label: 'Stop Loss Hit', value: 'Stop Loss Hit' },
                                                        { key: 3, label: 'Target Price Hit', value: 'Target Price Hit' },
                                                        { key: 4, label: 'Horizon Ended', value: 'Horizon Ended' },
                                                        { key: 5, label: 'Strategy Changed', value: 'Strategy Changed' },
                                                        { key: 6, label: 'Thesis Breaking Event', value: 'Thesis Breaking Event' },
                                                        { key: 7, label: 'Catastrophic Risk', value: 'Catastrophic Risk' },
                                                    ]}
                                                        placeholder="select version tag"
                                                        allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='versionTitle' label='Version Title'>
                                                    <Input placeholder="e.g., Stop Loss triggered at $100" allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='versionBody' label='Version Body'>
                                                    <Input.TextArea placeholder="Details about the exit" rows={3} allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </>
                        )
                    }
                    {
                        (defaultIdea && editProofOnly) && (
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.List name={'proofOfWork'}>
                                        {(subFields, subOpt) => (
                                            <div style={{ display: 'flex', flexDirection: 'column', rowGap: 16 }}>
                                                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Proof of Work</div>
                                                {subFields.map((subField) => (
                                                    <div key={subField.key} style={{ padding: 12, border: '1px dashed #eee', borderRadius: 4 }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                                            <span style={{ fontWeight: 500 }}>Item {subField.name + 1}</span>
                                                            <CloseOutlined
                                                                onClick={() => subOpt.remove(subField.name)}
                                                                style={{ cursor: 'pointer', color: '#ff4d4f' }}
                                                            />
                                                        </div>
                                                        <Row gutter={16}>
                                                            <Col span={12}>
                                                                <Form.Item label='ID' name={[subField.name, 'id']}>
                                                                    <Input placeholder="id" allowClear type='number' />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={12}>
                                                                <Form.Item label='Title' name={[subField.name, 'title']}>
                                                                    <Input placeholder="title" allowClear />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                        <Form.Item label='Item Type' name={[subField.name, 'type']}>
                                                            <Select options={[
                                                                { key: 1, label: 'FACTOR', value: 'factor' },
                                                                { key: 2, label: 'CONCLUSION', value: 'conclusion' },
                                                            ]}
                                                                placeholder="select item type"
                                                                allowClear />
                                                        </Form.Item>
                                                        <Form.Item label='Highlight' name={[subField.name, 'highlight']}>
                                                            <Input placeholder="highlight" allowClear />
                                                        </Form.Item>
                                                        <Form.Item label='Content' name={[subField.name, 'content']}>
                                                            <Input.TextArea placeholder="content" allowClear rows={3} />
                                                        </Form.Item>
                                                    </div>
                                                ))}
                                                <Button style={{ width: 200, fontSize: 12 }} type="dashed" onClick={() => subOpt.add()} block>
                                                    + Add Proof Item
                                                </Button>
                                            </div>
                                        )}
                                    </Form.List>
                                </Col>
                            </Row>
                        )
                    }
                </Form>
            </Drawer>
        </div>
    )
}

export default InvestmentIdeaDrawView;